import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useHeaderData } from './useHeaderData';
import { subscribeToCompanyMessages } from '@/lib/messagesRealtime';
import { toast } from 'sonner';

export const useUnreadMessages = () => {
  const { user } = useAuth();
  const { data: headerData } = useHeaderData();
  const companyId = headerData?.companyId ?? null;
  const queryClient = useQueryClient();

  const { data: unreadCount = 0 } = useQuery({
    queryKey: ['unreadMessages', companyId],
    queryFn: async () => {
      if (!companyId) return 0;
      const { count, error } = await supabase
        .from('platform_messages')
        .select('*', { count: 'exact', head: true })
        .eq('recipient_company_id', companyId)
        .eq('is_read', false);
      if (error) return 0;
      return count ?? 0;
    },
    enabled: !!companyId,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
  });

  // Realtime updates come from the single shared `messages:{companyId}` topic owner.
  useEffect(() => {
    if (!companyId) return;
    return subscribeToCompanyMessages(companyId, (event) => {
      if (!event.isIncoming) return;
      if (event.type === 'INSERT') {
        queryClient.setQueryData(['unreadMessages', companyId], (prev: number | undefined) => (prev ?? 0) + 1);
        toast.success('New message', { description: 'A conversation has new activity in Messages.' });
        return;
      }
      queryClient.invalidateQueries({ queryKey: ['unreadMessages', companyId] });
    });
  }, [companyId, queryClient]);

  return { unreadCount };
};
